import * as React from 'react';

import {
  LucideChevronDown,
  LucideCircleX,
  LucideLoader,
  LucideSearch,
} from 'lucide-react';
import { Dialog, ValidationResult } from 'react-aria-components';
import useResizeObserver from 'use-resize-observer';

import {
  cn,
  findNextFocusable,
  findPrevFocusable,
  isElementVisible,
} from '../../../lib';
import { FieldError, Label, fieldGroupVariants } from '../Field';
import { Popover } from '../overlays/Popover';
import { Tooltip } from '../overlays/Tooltip';

import { sortByGroup } from './sortByGroup';

export type ComboBoxOption<IDType extends string | number> = {
  id: IDType;
  label?: React.ReactNode;
  subLabel?: React.ReactNode;
  highlight?: React.ReactNode;
  group?: string;
};

type OptionRenderer<
  TOption extends ComboBoxOption<IDType>,
  IDType extends string | number = string | number,
> = (
  option: TOption,
  state: {
    selected: boolean;
    focused: boolean;
    index: number;
  },
) => React.ReactNode;

export type ComboBoxRef = {
  focus: () => void;
};

export type ComboBoxProps<
  IDType extends string | number = string | number,
  TOption extends ComboBoxOption<IDType> = ComboBoxOption<IDType>,
> = {
  fullHeight?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  isInvalid?: boolean;
  className?: string;
  tabIndex?: number;
  options: TOption[];
  showArrow?: boolean;
  openingTriggers?: ('click' | 'focus')[];
  renderOption?: OptionRenderer<TOption>;
  multi?: boolean;
  value?: IDType[];
  onChange?: (values: IDType[]) => void;
  query?: string;
  onChangeQuery?: (v: string) => void;
  onDismiss?: () => void;
  searchPlaceholder?: string;
  initialFocusedIndex?: number;
  label?: React.ReactNode;
  labelAdornment?: React.ReactNode;
  placeholder?: string;
  fieldRef?: React.RefObject<ComboBoxRef>;
  isLoading?: boolean;
  noSearch?: boolean;
  errorMessage?: string | ((validation: ValidationResult) => string);
  description?: string;
  compactSelection?: boolean;
  optionClassName?: string;
  autoFocus?: boolean;
  clearable?: boolean;
  renderEmpty?: (query: string) => React.ReactNode;
} & (
  | {
      renderSelection: (ids: IDType[]) => React.ReactNode;
      getSelection?: never;
    }
  | {
      getSelection: (
        ids: IDType[],
      ) => { label: React.ReactNode; subLabel?: React.ReactNode }[];
      renderSelection?: never;
    }
);

export const ComboBox = <
  IDType extends string | number,
  TOption extends ComboBoxOption<IDType> = ComboBoxOption<IDType>,
>({
  openingTriggers = ['click'],
  showArrow = true,
  renderSelection,
  getSelection,
  ...props
}: ComboBoxProps<IDType, TOption>) => {
  const [focusLocked, setFocusLocked] = React.useState(false);
  const [hasFocusWithin, setHasFocusWithin] = React.useState(false);
  const [currentFocusIndex, setCurrentFocusIndex] = React.useState(-1);
  const [isHovered, setIsHovered] = React.useState(false);

  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const popoverRootRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const optionsContainerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<Array<HTMLDivElement | null>>([]);

  const initialFocusedIndexRef = React.useRef(props.initialFocusedIndex);
  initialFocusedIndexRef.current = props.initialFocusedIndex;

  const uniqueId = React.useMemo(
    () => `combobox_${Math.random().toString(36).substring(2)}`,
    [],
  );

  const selectedIds = React.useMemo(
    () =>
      props.value === undefined
        ? []
        : Array.isArray(props.value)
          ? props.value
          : [props.value],
    [props.value],
  );

  const effectiveOptions = React.useMemo(
    () => sortByGroup(props.options),
    [props.options],
  );

  const currentSelectionIndex = React.useMemo(
    () => effectiveOptions?.findIndex((it) => props.value?.includes(it?.id)),
    [effectiveOptions, props.value],
  );

  const focusableElement = React.useCallback(
    () => document.querySelector(`#${uniqueId}`) as HTMLDivElement,
    [uniqueId],
  );

  const { width: triggerWidth } = useResizeObserver({
    ref: rootRef as never,
  });

  React.useImperativeHandle(
    props.fieldRef,
    () => ({
      focus: () => {
        focusableElement()?.focus();
      },
    }),
    [focusableElement],
  );

  React.useEffect(() => {
    if (props.autoFocus && !props.isDisabled) {
      requestAnimationFrame(() => {
        focusableElement()?.focus();
        setHasFocusWithin(true);
      });
    }
  }, [props.autoFocus, props.isDisabled, focusableElement]);

  const handleNewSelection = React.useCallback(
    (index = currentFocusIndex) => {
      const focusedOption = effectiveOptions[index];

      setHasFocusWithin(false);
      setFocusLocked(true);
      setTimeout(() => {
        focusableElement()?.focus();
        setFocusLocked(false);
      });

      setTimeout(() => {
        props.onChange?.(
          props.multi
            ? Array.from(
                new Set(
                  [...(props.value ?? []), focusedOption.id].filter(Boolean),
                ),
              )
            : [focusedOption?.id].filter(Boolean),
        );
      });
    },

    [
      currentFocusIndex,
      effectiveOptions,
      props.value,
      props.multi,
      props.onChange,
      focusableElement,
    ],
  );

  const handleKeydown = React.useCallback<React.KeyboardEventHandler>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setCurrentFocusIndex((i) =>
            Math.min(effectiveOptions.length - 1, i + 1),
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setCurrentFocusIndex((i) => Math.max(0, i - 1));
          break;

        case 'Escape':
          e.preventDefault();
          setHasFocusWithin(false);
          props.onDismiss?.();
          setTimeout(() => focusableElement()?.focus());
          break;

        case 'Enter':
          e.preventDefault();
          handleNewSelection();
          break;

        case 'Tab':
          e.preventDefault();
          setHasFocusWithin(false);
          if (e.shiftKey) {
            const prevFocusable = findPrevFocusable(focusableElement());
            setTimeout(() => prevFocusable?.focus(), 0);
          } else {
            const nextFocusable = findNextFocusable(focusableElement());
            setTimeout(() => nextFocusable?.focus(), 0);
          }
          break;

        default:
          // DO NOTHING
          break;
      }
    },

    [
      currentFocusIndex,
      effectiveOptions,
      props.onDismiss,
      focusableElement,
      handleNewSelection,
    ],
  );

  // When the available options change (e.g., data refresh), keep current index unless an initial index is provided
  React.useEffect(() => {
    setCurrentFocusIndex((i) => initialFocusedIndexRef.current ?? i);
  }, [effectiveOptions.length]);

  const currentSelectionIndexRef = React.useRef(currentSelectionIndex);
  currentSelectionIndexRef.current = currentSelectionIndex;

  // When the search query changes, reset focus to the first option
  React.useEffect(() => {
    setCurrentFocusIndex(props.query ? 0 : currentSelectionIndexRef.current);
  }, [props.query]);

  React.useLayoutEffect(() => {
    if (hasFocusWithin) {
      if (props.noSearch) {
        popoverRootRef.current?.focus();
      } else {
        searchInputRef.current?.focus();
      }
    } else {
      props.onChangeQuery?.('');
    }
  }, [hasFocusWithin, props.onChangeQuery, props.noSearch]);

  // Ensure focused option is scrolled into view within the options container
  React.useLayoutEffect(() => {
    if (!hasFocusWithin) return;
    const focusedEl = optionRefs.current[currentFocusIndex] ?? null;
    if (!focusedEl) return;

    if (!isElementVisible(focusedEl, optionsContainerRef.current)) {
      const beforeScroll = optionsContainerRef.current?.scrollTop ?? 0;
      focusedEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      const afterScroll = optionsContainerRef.current?.scrollTop ?? 0;

      if (afterScroll > beforeScroll) {
        // nudge the scroll position down by 2px so the focused option has some space above it
        optionsContainerRef.current?.scrollTo({
          top: afterScroll + 2,
          behavior: 'instant',
        });
      } else if (afterScroll < beforeScroll) {
        // nudge the scroll position up by 2px so the focused option has some space below it
        optionsContainerRef.current?.scrollTo({
          top: afterScroll - 2,
          behavior: 'instant',
        });
      }
    }
  }, [currentFocusIndex, hasFocusWithin, effectiveOptions.length]);

  // Keep option refs array in sync with options length
  React.useEffect(() => {
    optionRefs.current = new Array(effectiveOptions.length).fill(null);
  }, [effectiveOptions.length]);

  const renderOption: OptionRenderer<TOption, IDType> =
    props.renderOption ??
    ((it, state) => (
      <div
        className={cn(
          'mt-1 rounded px-3 py-1 pr-6',
          state.focused && 'bg-primary text-white',
          props.optionClassName,
        )}
      >
        {it?.subLabel ? (
          <>
            <div className="font-medium">{it?.label}</div>
            <div className="text-sm opacity-60">{it?.subLabel}</div>
          </>
        ) : (
          it?.label
        )}
      </div>
    ));

  const groupedOptions = React.useMemo(() => {
    const hasGroups = effectiveOptions.some((opt) => opt.group);
    if (!hasGroups) return null;

    const groups: {
      key: string;
      items: { option: TOption; globalIndex: number }[];
    }[] = [];
    const groupMap = new Map<
      string,
      { option: TOption; globalIndex: number }[]
    >();

    effectiveOptions.forEach((option, globalIndex) => {
      const groupKey = option.group ?? '';
      let items = groupMap.get(groupKey);
      if (!items) {
        items = [];
        groupMap.set(groupKey, items);
        groups.push({ key: groupKey, items });
      }
      items.push({ option, globalIndex });
    });

    return groups;
  }, [effectiveOptions]);

  const renderableSelection = React.useMemo(() => {
    if (selectedIds?.length && renderSelection) {
      return renderSelection(selectedIds);
    }

    if (selectedIds?.length && getSelection) {
      const selection = getSelection(selectedIds).at(0);
      const label = selection?.label || <>&nbsp;</>;
      const subLabel = selection?.subLabel;

      if (props.compactSelection) {
        const content = (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="truncate">{label}</span>
            {subLabel && (
              <>
                <span className="text-neutral/40">·</span>
                <span className="truncate text-sm text-neutral/40">
                  {subLabel}
                </span>
              </>
            )}
          </div>
        );

        const tooltipContent = subLabel ? (
          <>
            {label} · {subLabel}
          </>
        ) : (
          label
        );

        return (
          <Tooltip tip={tooltipContent} wrapWithButton={false} placement="top">
            <div className="cursor-pointer px-1">{content}</div>
          </Tooltip>
        );
      }

      return (
        <div className="cursor-pointer px-1">
          <div>{label}</div>
          {subLabel && (
            <div className="text-sm text-neutral/40">{subLabel}</div>
          )}
        </div>
      );
    }

    return (
      <div className="px-1 opacity-50">{props.placeholder || <>&nbsp;</>}</div>
    );
  }, [
    selectedIds,
    renderSelection,
    getSelection,
    props.placeholder,
    props.compactSelection,
  ]);

  const isInvalid = props.isInvalid || !!props.errorMessage;

  return (
    <div className="@ComboBox contents">
      <div
        ref={rootRef}
        className={cn(
          'relative flex flex-col gap-2',
          props.fullHeight && 'h-full',
          props.className,
        )}
      >
        {props.label && (
          <Label
            aria-required={props.isRequired}
            adornment={props.labelAdornment}
            className={cn(isInvalid && 'text-failure')}
          >
            {props.label}
          </Label>
        )}
        <div
          ref={triggerRef}
          id={uniqueId}
          tabIndex={props.isDisabled ? undefined : (props.tabIndex ?? 0)}
          className={fieldGroupVariants({
            className: cn(
              'h-auto cursor-pointer',
              props.isDisabled && 'cursor-not-allowed',
              props.className,
            ),
            focusWithin: !!hasFocusWithin,
            disabled: props.isDisabled,
            invalid: isInvalid,
          })}
          // data-focus-within={hasFocusWithin}
          onClick={() => {
            if (
              !focusLocked &&
              openingTriggers?.includes('click') &&
              !hasFocusWithin
            ) {
              setHasFocusWithin(true);
            }
          }}
          onFocus={() => {
            if (!focusLocked && openingTriggers?.includes('focus')) {
              setHasFocusWithin(true);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setHasFocusWithin(true);
          }}
          onKeyDown={(e) => {
            if (
              e.key !== 'Tab' &&
              e.key !== 'Escape' &&
              e.key !== 'Shift' &&
              e.key !== 'Meta' &&
              e.key !== 'Control' &&
              e.key !== 'Alt'
            ) {
              setHasFocusWithin(true);
            }
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className={'min-w-0 flex-1'}>{renderableSelection}</div>
          <div className={'flex shrink-0 gap-2'}>
            {props.clearable &&
            !props.multi &&
            selectedIds.length > 0 &&
            isHovered &&
            !props.isDisabled ? (
              <button
                type="button"
                className="flex items-center justify-center text-neutral/40 hover:text-neutral/60"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onChange?.([]);
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <LucideCircleX className="size-[1.125em] cursor-pointer fill-neutral/40 text-white" />
              </button>
            ) : (
              showArrow &&
              (props.isLoading ? (
                <LucideLoader
                  className={'size-[1.125em] animate-spin text-neutral/40'}
                />
              ) : (
                <LucideChevronDown
                  className={'size-[1.125em] text-neutral/40'}
                />
              ))
            )}
          </div>
        </div>
        {props.errorMessage && <FieldError>{props.errorMessage}</FieldError>}
      </div>
      <Popover
        isOpen={!props.isDisabled && hasFocusWithin}
        onOpenChange={(isOpen) => {
          setHasFocusWithin(isOpen);
          setFocusLocked(true);
          setTimeout(() => setFocusLocked(false), 500);
        }}
        placement="bottom left"
        shouldCloseOnInteractOutside={() => true}
        triggerRef={triggerRef}
      >
        <Dialog>
          <div
            ref={popoverRootRef}
            id={`${uniqueId}-popover`}
            tabIndex={-1}
            onFocus={() => setHasFocusWithin(true)}
            className={
              'box-border flex max-h-[30vh] flex-col focus:outline-none focus-visible:outline-none'
            }
            style={{ minWidth: `${triggerWidth}px` }}
            onKeyDown={handleKeydown}
          >
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2',
                (props.query || effectiveOptions.length) &&
                  'border-b border-solid border-neutral/20',
                props.noSearch && 'hidden',
              )}
            >
              <LucideSearch size={16} className={'text-neutral opacity-60'} />
              <input
                ref={searchInputRef}
                placeholder={props.searchPlaceholder ?? 'Search'}
                className={
                  'w-full border-none bg-transparent text-sm text-neutral outline-none'
                }
                type="text"
                value={props.query}
                onChange={(e) => props.onChangeQuery?.(e.target.value)}
              />
            </div>
            {(props.query || !!effectiveOptions.length) && (
              <div
                ref={optionsContainerRef}
                className="no-scrollbar h-full overflow-auto px-0.5 pb-2 text-sm"
              >
                {groupedOptions
                  ? groupedOptions.map((group) => (
                      <div key={group.key || '__default'}>
                        {group.key && (
                          <div className="mt-2 mb-1 px-3 text-xs font-semibold tracking-wider text-neutral/60 uppercase">
                            {group.key} ({group.items.length})
                          </div>
                        )}
                        {group.items.map(({ option, globalIndex }) => (
                          <div
                            key={option?.id}
                            ref={(el) => {
                              optionRefs.current[globalIndex] = el;
                            }}
                            onMouseDown={() => handleNewSelection(globalIndex)}
                            onMouseEnter={() =>
                              setCurrentFocusIndex(globalIndex)
                            }
                            className={'cursor-pointer'}
                          >
                            {renderOption(option, {
                              selected: currentSelectionIndex === globalIndex,
                              focused: currentFocusIndex === globalIndex,
                              index: globalIndex,
                            })}
                          </div>
                        ))}
                      </div>
                    ))
                  : effectiveOptions?.map((option, index) => (
                      <div
                        key={option?.id}
                        ref={(el) => {
                          optionRefs.current[index] = el;
                        }}
                        onMouseDown={() => handleNewSelection(index)}
                        onMouseEnter={() => setCurrentFocusIndex(index)}
                        className={'cursor-pointer'}
                      >
                        {renderOption(option, {
                          selected: currentSelectionIndex === index,
                          focused: currentFocusIndex === index,
                          index,
                        })}
                      </div>
                    ))}
                {!effectiveOptions.length &&
                  !props.isLoading &&
                  (props.renderEmpty ? (
                    props.renderEmpty(props.query ?? '')
                  ) : (
                    <div className="p-3">No options available</div>
                  ))}
              </div>
            )}
          </div>
        </Dialog>
      </Popover>
    </div>
  );
};
